import ProjectCard from '../../components/ProjectCard/ProjectCard.tsx'
import SectionHeader from '../../components/SectionHeader/SectionHeader.tsx'

type TileColor = 'default' | 'cream' | 'hazard' | 'hot' | 'cobalt' | 'teal'

interface TileData {
  lab: string
  titleBefore: string
  titleItalic: string
  description: string
  tags: string
  color: TileColor
}

const TILES: TileData[] = [
  {
    lab: 'AI & ML',
    titleBefore: 'AI for',
    titleItalic: 'publishing.',
    description:
      'LLM tooling, RAG patterns, prompt design and evals — applied to editorial work with care.',
    tags: 'LLMs · RAG · Evals · Prompt Patterns',
    color: 'default',
  },
  {
    lab: 'Workflow Efficiency',
    titleBefore: 'Making the work',
    titleItalic: 'flow.',
    description:
      'Removing friction from editorial & engineering pipelines. Automation where it earns its keep; restraint everywhere else.',
    tags: 'Automation · CI/CD · Editorial Tooling · Toil Reduction',
    color: 'hazard',
  },
  {
    lab: 'Process Mapping',
    titleBefore: 'Mapping the',
    titleItalic: 'terrain.',
    description:
      'Process modelling, value-stream mapping and BPMN where it helps a team see itself clearly — before redesigning anything.',
    tags: 'BPMN · Value Streams · Service Design',
    color: 'cream',
  },
  {
    lab: 'AI Engineering',
    titleBefore: 'AI features,',
    titleItalic: 'built to last.',
    description:
      'Building AI-powered features the publishing team actually trusts. Eval harnesses, observability, and the quiet plumbing behind "magic".',
    tags: 'LLM Apps · Eval Harness · Observability · Pipelines',
    color: 'cobalt',
  },
  {
    lab: 'Engineering Leadership',
    titleBefore: 'Teams, careers,',
    titleItalic: 'strategy.',
    description:
      'Building, growing and steering engineering teams across multiple concurrent initiatives.',
    tags: 'Team Building · Performance · Hiring · Cross-team',
    color: 'teal',
  },
  {
    lab: 'Content Management',
    titleBefore: 'CMS &',
    titleItalic: 'editorial systems.',
    description:
      'Deep practical knowledge of CMS architecture, multi-tenant publishing, and the workflows behind a newsroom.',
    tags: 'WordPress · Gutenberg · React CMS · Multi-tenant',
    color: 'default',
  },
  {
    lab: 'Full-Stack',
    titleBefore: 'Web at',
    titleItalic: 'scale.',
    description: 'Modern, opinionated stacks. Type-safe by default, observable by design.',
    tags: 'React · TypeScript · GraphQL · Node · Postgres',
    color: 'hot',
  },
  {
    lab: 'Media & Publishing',
    titleBefore: 'High-traffic',
    titleItalic: 'publishing.',
    description:
      'Newsroom workflows, content distribution and resilient delivery on busy days.',
    tags: 'Distribution · Newsroom Tools · High Availability',
    color: 'cream',
  },
  {
    lab: 'Strategy',
    titleBefore: 'Migrations, redesigns,',
    titleItalic: 're-platforming.',
    description: 'Planning and executing the kind of work that takes more than one quarter.',
    tags: 'Architecture · Migration · Performance · Roadmaps',
    color: 'default',
  },
]

const SkillsSection = () => (
  <section className="px-6 py-16 border-b-4 border-ink">
    <SectionHeader
      number="01"
      variant="hot"
      titlePrimary="Engineering"
      titleSecondary="practice."
      subtitle="The areas I keep my hands in — from AI in publishing to the unglamorous work of mapping how a team actually gets things done."
      metaLines={['SECTION 01', 'PRACTICE AREAS', '09 DOMAINS']}
    />

    <div className="grid grid-cols-1 md:grid-cols-3 border-2 border-ink max-w-[1600px]">
      {TILES.map((tile, index) => (
        <ProjectCard
          key={tile.lab}
          color={tile.color}
          lab={tile.lab}
          title={
            <>
              {tile.titleBefore}{' '}
              <span className="font-serif italic font-medium normal-case">{tile.titleItalic}</span>
            </>
          }
          body={tile.description}
          tags={tile.tags}
          isLastCol={(index + 1) % 3 === 0}
          hasBorderBottom={index < 6}
          isLastMobile={index === TILES.length - 1}
        />
      ))}
    </div>
  </section>
)

export default SkillsSection
